import { Button, Notification } from "@rio-cloud/rio-uikit";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import { useState, useRef } from "react";
import { downloadClientsTemplate, uploadClientsBatch } from "../../../services/form";
import { ClientUploadData } from "../../../types";

interface Props {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const DialogUploadClients = ({ show, onClose, onSuccess }: Props) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [result, setResult] = useState<ClientUploadData | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClose = () => {
    const hadSuccess = (result?.successCount ?? 0) > 0;
    setSelectedFile(null);
    setResult(null);
    setIsUploading(false);
    onClose();
    if (hadSuccess) {
      onSuccess();
    }
  };

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const blob = await downloadClientsTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "template-clientes.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      Notification.error("Erro ao baixar a planilha modelo.");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      const response = await uploadClientsBatch(selectedFile);
      setResult(response.data);
      if (response.data.errorCount === 0) {
        Notification.success(`${response.data.successCount} cliente(s) importado(s) com sucesso.`);
      } else if (response.data.successCount > 0) {
        Notification.warning(`${response.data.successCount} cliente(s) importado(s), com ${response.data.errorCount} erro(s).`);
      } else {
        Notification.error("Nenhum cliente foi importado. Verifique os erros apontados.");
      }
    } catch (error: any) {
      const errorData = error?.response?.data?.data as ClientUploadData | undefined;
      if (errorData?.errors?.length) {
        setResult(errorData);
        Notification.error("Erros encontrados no arquivo. Verifique a lista abaixo.");
      } else {
        const message =
          error?.response?.data?.message || error?.response?.data?.detail || "Erro ao processar o arquivo de clientes.";
        Notification.error(message);
      }
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog
      show={show}
      title={<span className="text-medium text-size-16">Importar Clientes em Lote</span>}
      body={
        <div className="padding-15">
          <div className="display-flex justify-content-between align-items-center padding-10 background-color-gray-light rounded-small margin-bottom-15">
            <div>
              <strong>Planilha Modelo</strong>
              <div className="text-size-12 text-color-muted">Baixe o modelo predefinido com as colunas necessárias.</div>
            </div>
            <Button
              bsStyle={Button.DEFAULT}
              onClick={handleDownload}
              disabled={isDownloading}
              className="rounded-small"
            >
              <span className="rioglyph rioglyph-download margin-right-5" />
              {isDownloading ? "Baixando..." : "Baixar Modelo"}
            </Button>
          </div>

          <div className="form-group margin-bottom-15">
            <label htmlFor="fileUploadInput">
              <span>Selecione a planilha preenchida (.xlsx, .xls)</span>
            </label>
            <input
              ref={fileInputRef}
              id="fileUploadInput"
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileChange}
              className="form-control"
              disabled={isUploading}
            />
            {selectedFile && (
              <div className="text-size-12 margin-top-5 text-color-muted">
                Arquivo selecionado: <strong>{selectedFile.name}</strong> ({(selectedFile.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {result && (
            <div className="margin-top-15">
              <div className="display-flex gap-10 margin-bottom-10">
                <span className="badge badge-info">Total: {result.totalRows}</span>
                <span className="badge badge-success">Sucessos: {result.successCount}</span>
                {result.errorCount > 0 && <span className="badge badge-danger">Erros: {result.errorCount}</span>}
              </div>

              {result.errors && result.errors.length > 0 && (
                <div style={{ maxHeight: "200px", overflowY: "auto" }} className="border-all rounded-small">
                  <table className="table table-bordered table-striped margin-bottom-0 text-size-12">
                    <thead>
                      <tr>
                        <th style={{ width: "60px" }}>Linha</th>
                        <th style={{ width: "140px" }}>CNPJ / CPF</th>
                        <th>Nome</th>
                        <th>Motivo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.errors.map((err, idx) => (
                        <tr key={idx}>
                          <td>{err.rowNumber}</td>
                          <td>{err.document || "-"}</td>
                          <td>{err.name || "-"}</td>
                          <td className="text-color-danger">{err.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      }
      onClose={handleClose}
      footer={
        <div className="display-flex align-items-center justify-content-end gap-10">
          <Button onClick={handleClose} className="width-150 rounded-small">
            {result ? "Fechar" : "Cancelar"}
          </Button>
          {!result && (
            <Button
              onClick={handleUpload}
              bsStyle={Button.PRIMARY}
              className="width-150 rounded-small"
              disabled={!selectedFile || isUploading}
            >
              {isUploading ? "Processando..." : "Importar"}
            </Button>
          )}
        </div>
      }
      showCloseButton
      useOverflow
    />
  );
};

export default DialogUploadClients;
